/**
 * Landing — AADC flagship homepage (Clubhouse / Local-first).
 *
 * The old 3-column social-feed shell was replaced with a directory-first
 * landing that funnels visitors straight into /discover and Charter
 * membership. Design tokens: Forest Pine + Terracotta + Honey Gold on
 * Bone-canvas (see /app/design_guidelines.json).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO, { HOME_FAQ_ITEMS } from '../components/SEO';
import GuestEngagementWidget from '../components/GuestEngagementWidget';
import BetaNoticeBanner from '../components/BetaNoticeBanner';
import { InFeedAd } from '../components/AdSense';
import { CharterCounter } from '../components/CharterCounter';
import LatestVideosWidget from '../components/LatestVideosWidget';
import AskTheClub from '../components/AskTheClub';
import { AdoptionSection } from '../components/landing/LandingComponents';
import {
  KennelClubsMini,
  PartnersMini,
} from '../components/landing/LandingSidebarCards';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MapPin, Compass, Heart, Shield, Dna, Users,
  PawPrint, Globe, ArrowRight, Sparkles, Stethoscope,
  TreeDeciduous, Search,
} from 'lucide-react';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1200&q=80';

const CATEGORY_CHIPS = [
  { key: 'events', label: 'Events near me', icon: Compass },
  { key: 'ngos', label: 'Rescues', icon: Heart },
  { key: 'independents', label: 'Trusted vets & shops', icon: Stethoscope },
  { key: 'meetup', label: 'Meetups', icon: Users },
  { key: 'training', label: 'Training', icon: PawPrint },
];

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [zip, setZip] = useState('');

  const goDiscover = (topic) => {
    const params = new URLSearchParams();
    if (topic) params.set('topic', topic);
    if (zip.trim()) params.set('q', zip.trim());
    const qs = params.toString();
    navigate(qs ? `/discover?${qs}` : '/discover');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <SEO
        title={null}
        description="All American Dog Club (AADC) is a member-governed online community for dog lovers and dog clubs. Multi-club platform, health resources, local parks, partner rescues, and revenue-share for partner organizations."
        faqItems={HOME_FAQ_ITEMS}
        keywords={[
          'All American Dog Club', 'AADC', 'americandogclub.com',
          'dog club platform', 'multi-club dog community',
          'dog breeds', 'dog training', 'dog health',
          'dog parks near me', 'dog events', 'dog rescue',
        ]}
        url="/"
      />
      <GuestEngagementWidget />
      <BetaNoticeBanner />

      {/* ============================================================
          HERO — asymmetric split: copy left, hero image right.
          The search bar feeds directly into /discover.
          ============================================================ */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-background to-secondary/[0.05] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 md:pt-20 md:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          {/* Copy column */}
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-mono uppercase tracking-widest text-primary">
              <Sparkles className="w-3 h-3" /> Member-owned · Local-first
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.05]">
              Your dog's <span className="italic text-primary">local clubhouse</span>
              <br className="hidden sm:block" />
              & trusted national directory.
            </h1>
            <p className="mt-5 text-lg font-body text-muted-foreground leading-relaxed max-w-2xl">
              A member-owned co-op for dog people. Find local events, rescues, and vetted independents near you — plus DNA, health records, and 121 kennel clubs worldwide. Your data is never sold.
            </p>

            {/* Search bar → /discover */}
            <form
              onSubmit={(e) => { e.preventDefault(); goDiscover(); }}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
              data-testid="landing-search-form"
            >
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                <Input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="Your city or zip"
                  className="pl-11 h-12 rounded-full border-border/70 bg-card focus-visible:ring-primary"
                  data-testid="landing-zip-input"
                />
              </div>
              <Button
                type="submit"
                className="h-12 rounded-full px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-soft hover:shadow-hover transition-all active:scale-95"
                data-testid="landing-explore-btn"
              >
                <Search className="w-4 h-4 mr-2" /> Explore near me
              </Button>
            </form>

            {/* Category chips */}
            <div className="mt-5 flex flex-wrap gap-2" data-testid="landing-category-chips">
              {CATEGORY_CHIPS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => goDiscover(key)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                  data-testid={`landing-chip-${key}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            {/* Trust row */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-primary" /> Data never sold</span>
              <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-primary" /> Member-governed</span>
              <span className="inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-primary" /> 121 kennel clubs</span>
            </div>
          </div>

          {/* Image column — organic frame */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-secondary/10 rotate-2" aria-hidden="true" />
              <div className="relative rounded-[2rem] overflow-hidden shadow-hover border border-border/60 bg-primary/10">
                <img
                  src={HERO_IMAGE}
                  alt="Dogs enjoying a local community park"
                  className="w-full h-[380px] md:h-[480px] object-cover"
                  loading="eager"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <Card className="border-border/40 bg-card/95 backdrop-blur">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        <PawPrint className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-heading font-semibold text-foreground text-sm">Charter seats going fast</p>
                        <p className="text-xs text-muted-foreground font-mono">Founding members lock lifetime perks.</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-primary" />
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TODAY — FROM THE CLUB (AARP-style editorial rail)
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 md:mt-10" data-testid="landing-today-rail">
        <div className="rounded-2xl border border-border/70 bg-card p-6 md:p-8 shadow-soft">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <p className="font-mono uppercase tracking-widest text-[11px] text-primary mb-1">Today · From the Club</p>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground tracking-tight">What's new for dog people</h2>
            </div>
            <button
              onClick={() => navigate('/club-blog')}
              className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/70 transition-colors font-mono uppercase tracking-wider"
              data-testid="landing-today-more-link"
            >
              More <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <TodayItem
              prefix="Fighting for Dogs"
              title="Why breed-specific-legislation still matters in 2026"
              href="/club-blog"
              testid="today-fighting"
            />
            <TodayItem
              prefix="Save at Local Vets"
              title="Find member-only discounts at Trusted Independents near you"
              href="/discover?topic=independents"
              testid="today-save"
            />
            <TodayItem
              prefix="Meet the Pack"
              title="Charter members introduce their dogs — new arrivals this week"
              href="/discover"
              testid="today-meet"
            />
            <TodayItem
              prefix="Live Well Together"
              title="A vet's five-minute morning routine for senior dogs"
              href="/club-blog"
              testid="today-live"
            />
          </div>
        </div>
      </section>

      {/* ============================================================
          CHARTER MEMBER SCARCITY
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="landing-charter-section">
        <div className="rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/[0.08] via-accent/[0.08] to-secondary/[0.05] p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex-1">
              <p className="font-mono uppercase tracking-widest text-[11px] text-secondary mb-1">Founding cohort</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Charter Membership · Locked-in perks for life</h2>
            </div>
            <div className="md:min-w-[260px]"><CharterCounter /></div>
          </div>
        </div>
      </section>

      {/* ============================================================
          ADOPTION STRIP
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="landing-adoption-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="font-mono uppercase tracking-widest text-[11px] text-primary mb-2">Local rescues</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground tracking-tight">Dogs looking for a home</h2>
          </div>
          <button
            onClick={() => navigate('/adopt')}
            className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/70 transition-colors"
            data-testid="landing-view-adoption-link"
          >
            View all <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <AdoptionSection />
      </section>

      {/* ============================================================
          VALUE PROPS — BENTO GRID (asymmetric 12-col)
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24" data-testid="landing-value-props">
        <div className="mb-10">
          <p className="font-mono uppercase tracking-widest text-[11px] text-primary mb-2">The clubhouse</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground tracking-tight max-w-2xl">Everything a dog family actually needs — in one member-owned home.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <BentoCard
            className="md:col-span-8 bg-primary text-primary-foreground"
            badge="Verified lineage"
            icon={Dna}
            title="DNA testing & family trees"
            body="Discover breed composition, health markers, and connect with verified breeding programs. Interactive ancestry, so your pet's story is always readable."
            cta={{ label: 'Explore breeds', to: '/breeds' }}
            testid="bento-dna"
          />
          <BentoCard
            className="md:col-span-4"
            badge="Encrypted"
            icon={Shield}
            title="Health records vault"
            body="Vet-shareable, portable. Yours to keep."
            testid="bento-health"
          />
          <BentoCard
            className="md:col-span-4"
            badge="Vetted"
            icon={Stethoscope}
            title="Trusted independents"
            body="Local vets, groomers, boarding — nominated & member-reviewed."
            cta={{ label: 'See directory', to: '/discover?topic=independents' }}
            testid="bento-independents"
          />
          <BentoCard
            className="md:col-span-8 bg-secondary/10 border-secondary/30"
            badge="Member moderated"
            icon={Users}
            title="Topic-based communities"
            body="Breed-specific groups, sport meetups, training circles. Local + global."
            cta={{ label: 'Browse communities', to: '/communities' }}
            testid="bento-communities"
          />
          <BentoCard
            className="md:col-span-6"
            badge="121 worldwide"
            icon={Globe}
            title="Kennel clubs directory"
            body="Argentina to Zimbabwe — with FCI status and official links."
            cta={{ label: 'Open the directory', to: '/kennel-clubs' }}
            testid="bento-kennel-clubs"
          />
          <BentoCard
            className="md:col-span-6"
            badge="Make a difference"
            icon={TreeDeciduous}
            title="Research & rescue partners"
            body="Opt into veterinary studies. Support partner rescues via member co-op revenue share."
            cta={{ label: 'Meet partners', to: '/partners' }}
            testid="bento-research"
          />
        </div>
      </section>

      {/* ============================================================
          WHY JOIN — three-column principles
          ============================================================ */}
      <section className="bg-primary/[0.04] border-y border-border/60" data-testid="landing-why-join">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="font-mono uppercase tracking-widest text-[11px] text-primary mb-2">Co-op principles</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground tracking-tight">Owned by members. Built for dogs.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <Principle
              icon={Shield}
              title="Your data is never sold"
              body="We publish our privacy policy in plain English. Zero data-broker relationships. Ever."
            />
            <Principle
              icon={Dna}
              title="Tools that pay you back"
              body="DNA, health records, and ancestry are built for dog families first — not for advertisers."
            />
            <Principle
              icon={Users}
              title="Local clubs, global reach"
              body="Charter seats in your neighborhood cooperative. Access to 121 kennel clubs worldwide."
            />
          </div>
          {!user && (
            <div className="mt-12 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/signup')}
                className="h-12 rounded-full px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-soft hover:shadow-hover transition-all active:scale-95"
                data-testid="landing-signup-cta"
              >
                Claim your Charter seat
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/discover')}
                className="h-12 rounded-full px-6 border-2 border-primary/20 hover:border-primary text-primary hover:bg-primary/5 font-semibold transition-all"
                data-testid="landing-discover-cta"
              >
                <Compass className="w-4 h-4 mr-2" /> Browse the directory
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ============================================================
          Kennel clubs + Partners strip
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-8">
        <KennelClubsMini />
        <PartnersMini />
      </section>

      {/* In-feed ad slot */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <InFeedAd slot="landingInFeed" />
      </section>

      {/* ============================================================
          LATEST VIDEOS
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24" data-testid="landing-videos-section">
        <div className="mb-6">
          <p className="font-mono uppercase tracking-widest text-[11px] text-primary mb-2">Field notes</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground tracking-tight">Latest from the club</h2>
        </div>
        <LatestVideosWidget limit={6} />
      </section>

      {/* ============================================================
          BOTTOM CTA
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-10 md:p-16">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
          <div className="relative max-w-2xl">
            <p className="font-mono uppercase tracking-widest text-[11px] text-accent mb-3">Join the pack</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold leading-tight">
              A better home for your dog's life — <span className="italic text-accent">and yours.</span>
            </h2>
            <p className="mt-5 text-primary-foreground/80 text-lg font-body leading-relaxed">
              Charter memberships are limited. Lock in lifetime perks, priority local access, and co-op voting rights before the founding cohort closes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/signup')}
                className="h-12 rounded-full px-6 bg-accent hover:bg-accent/90 text-foreground font-semibold shadow-soft transition-all active:scale-95"
                data-testid="landing-bottom-signup"
              >
                Claim Charter Membership
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/discover')}
                className="h-12 rounded-full px-6 border-2 border-primary-foreground/30 hover:border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 font-semibold transition-all"
                data-testid="landing-bottom-discover"
              >
                <MapPin className="w-4 h-4 mr-2" /> Find services near you
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Floating "Ask the Club" AI widget (AARP-style) */}
      <AskTheClub />
    </div>
  );
};

// ==========================
// Sub-components
// ==========================

function BentoCard({ className = '', badge, icon: Icon, title, body, cta, testid }) {
  const navigate = useNavigate();
  const isDark = className.includes('bg-primary text-primary-foreground');
  return (
    <div
      className={`group rounded-2xl border border-border/60 bg-card p-6 md:p-8 shadow-soft hover:shadow-hover hover:-translate-y-0.5 transition-all duration-300 ${className}`}
      data-testid={testid}
    >
      {badge && (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest ${isDark ? 'bg-accent/20 text-accent' : 'bg-primary/10 text-primary'}`}>
          {badge}
        </span>
      )}
      <div className={`mt-4 inline-flex items-center justify-center w-11 h-11 rounded-xl ${isDark ? 'bg-primary-foreground/10 text-accent' : 'bg-primary/10 text-primary'}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className={`mt-4 font-heading text-xl md:text-2xl font-semibold ${isDark ? 'text-primary-foreground' : 'text-foreground'}`}>{title}</h3>
      <p className={`mt-2 text-base font-body leading-relaxed ${isDark ? 'text-primary-foreground/75' : 'text-muted-foreground'}`}>{body}</p>
      {cta && (
        <button
          onClick={() => navigate(cta.to)}
          className={`mt-5 inline-flex items-center gap-1 text-sm font-semibold transition-colors ${isDark ? 'text-accent hover:text-accent/80' : 'text-primary hover:text-primary/70'}`}
        >
          {cta.label} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function Principle({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl bg-card border border-border/60 p-6 shadow-soft">
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="mt-4 font-heading text-xl font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-base font-body text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}

function TodayItem({ prefix, title, href, testid }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(href)}
      className="group block w-full text-left rounded-xl border border-border/60 bg-background hover:bg-card hover:border-primary/40 transition-all p-4 md:p-5"
      data-testid={testid}
    >
      <p className="font-mono uppercase tracking-widest text-[10px] text-secondary mb-2">{prefix}</p>
      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
        {title}
      </h3>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary/80 group-hover:text-primary transition-colors">
        Read <ArrowRight className="w-3 h-3" />
      </span>
    </button>
  );
}

export default Landing;
